import { useEffect, useState } from 'react';
import { Box, Button, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { allocationSaveState, MODES } from '../utils';
import { BORDER, buttonSx, captionSx, DANGER, eyebrowSx, INK, inputSx, MUTED } from './styles';

const OPTIONS = [
  {
    value: MODES.SHARED,
    label: 'Shared pool',
    hint: 'Everyone draws from the organisation pool.',
    icon: GroupsOutlinedIcon,
  },
  {
    value: MODES.PER_USER,
    label: 'Per user',
    hint: 'Each member gets their own allowance each period.',
    icon: PersonOutlineIcon,
  },
];

const optionSx = {
  flex: 1,
  minWidth: { xs: '100%', sm: 200 },
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  textAlign: 'left',
  textTransform: 'none',
  gap: 1.25,
  p: '12px 14px',
  borderRadius: '10px !important',
  border: `1px solid ${BORDER} !important`,
  color: INK,
  backgroundColor: '#fff',
  '&:hover': { backgroundColor: '#FAFBFA' },
  '&.Mui-selected, &.Mui-selected:hover': {
    borderColor: (theme) => `${theme.palette.primary.main} !important`,
    boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.primary.main}`,
    backgroundColor: '#fff',
  },
};

// Mode toggle + default allowance for one product.
export default function AllocationPanel({ product, allocation, busy, onSave }) {
  const storedMode = allocation?.mode || MODES.SHARED;
  const storedDefault = allocation?.defaultUserAllowance ?? null;

  const [mode, setMode] = useState(storedMode);
  const [defaultText, setDefaultText] = useState(storedDefault === null ? '' : String(storedDefault));
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    setMode(storedMode);
    setDefaultText(storedDefault === null ? '' : String(storedDefault));
    setLocalError(null);
  }, [storedMode, storedDefault]);

  const { canSave, error, body } = allocationSaveState({ storedMode, storedDefault, mode, defaultText });

  const handleSave = () => {
    if (error) {
      setLocalError(error);
      return;
    }
    setLocalError(null);
    onSave(body);
  };

  const shownError = localError || (!canSave ? error : null);
  const perUser = mode === MODES.PER_USER;

  return (
    <Box data-testid={`allocation-${product}`}>
      <Typography sx={{ ...eyebrowSx, mb: 1.25 }}>How credits are shared</Typography>
      <ToggleButtonGroup
        exclusive
        value={mode}
        onChange={(_, next) => {
          if (next) {
            setMode(next);
            setLocalError(null);
          }
        }}
        aria-label="Allocation mode"
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}
      >
        {OPTIONS.map(({ value, label, hint, icon: Icon }) => {
          const selected = mode === value;
          return (
            <ToggleButton key={value} value={value} sx={optionSx}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  mt: '2px',
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: selected ? 'primary.main' : '#c4c9c4',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'primary.main' }} />}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Icon sx={{ fontSize: 17, color: INK }} />
                  <Typography sx={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: { xs: '14px', sm: '13px', xl: '14px' }, color: INK }}>
                    {label}
                  </Typography>
                </Box>
                <Typography sx={{ ...captionSx, mt: 0.25 }}>{hint}</Typography>
              </Box>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5, mt: 2 }}>
        {perUser && (
          <TextField
            size="small"
            label="Default user allowance"
            value={defaultText}
            onChange={(e) => {
              setDefaultText(e.target.value);
              setLocalError(null);
            }}
            error={!!shownError}
            helperText="Credits per member, per period"
            FormHelperTextProps={{ sx: { fontFamily: 'Rubik', ml: 0.25, color: MUTED } }}
            inputProps={{ inputMode: 'numeric' }}
            sx={{ ...inputSx, width: { xs: '100%', sm: 220 } }}
          />
        )}
        <Button
          variant="contained"
          disableElevation
          disabled={busy || !canSave}
          onClick={handleSave}
          sx={{ ...buttonSx, width: { xs: '100%', sm: 'auto' }, mt: perUser ? { sm: '1px' } : 0 }}
        >
          {busy ? 'Saving…' : 'Save allowance'}
        </Button>
      </Box>
      {shownError && (
        <Typography role="alert" sx={{ ...captionSx, color: DANGER, mt: 1 }}>{shownError}</Typography>
      )}
    </Box>
  );
}
